import { RefObject, useCallback, useLayoutEffect } from 'react';

/**
 * Grows a textarea to its content between the floor its `rows` attribute sets
 * and `maxHeightPx`. CSS field-sizing does the same thing in one line but is
 * absent from two of this project's declared production targets
 * (firefox >= 128, safari >= 16.4), where the box would stay one row and scroll.
 */
export function useAutoGrowTextarea(
  ref: RefObject<HTMLTextAreaElement | null>,
  value: string,
  maxHeightPx: number,
) {
  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    // Clearing height first lets the `rows` attribute set the natural floor, so
    // the minimum tracks the font rather than a hardcoded pixel guess.
    el.style.height = '';
    const floor = el.clientHeight;
    // Then measure the content, which shrinks as well as grows.
    el.style.height = 'auto';
    el.style.height = `${Math.min(Math.max(el.scrollHeight, floor), maxHeightPx)}px`;
  }, [ref, maxHeightPx]);

  // Layout effect, not an effect: measuring after paint shows one frame at the
  // stale height, which flickers on every keystroke that adds a line.
  useLayoutEffect(resize, [value, resize]);

  return resize;
}
